import { zodResolver } from '@hookform/resolvers/zod'
import { Mail, Phone } from 'lucide-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'

import { useSendReferralStore } from 'src/features/business/referrals/stores/send-referral-store'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { InputFormField } from 'src/shared/components/ui/forms/form-fields/input'
import { FixedFooter } from 'src/shared/components/ui/miscellaneous/fixed-footer'
import { useCreateConsumer } from 'src/shared/features/consumers/api/use-create-referral-client'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useToast } from 'src/shared/hooks/use-toast'
import type { ClientInfoType } from 'src/shared/schemas/referrals/referral'
import { clientInfoSchema } from 'src/shared/schemas/referrals/referral'
import { useEventStore } from 'src/shared/store/event-store'

export const ClientInfoForm: React.FC = () => {
  const referralFormData = useSendReferralStore((state) => state.referralFormData)
  const handleNext = useSendReferralStore((state) => state.handleNext)
  const handleBack = useSendReferralStore((state) => state.handleBack)
  const { data: currentUser } = useMyUserData()
  const eventStore = useEventStore()
  const sessionId = eventStore.sessionId
  const flowId = eventStore.flowId
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const createConsumer = useCreateConsumer()

  const form = useForm<ClientInfoType>({
    resolver: zodResolver(clientInfoSchema),
    mode: 'onSubmit',
    defaultValues: {
      clientFirstName: (referralFormData.clientFirstName as string) || '',
      clientLastName: (referralFormData.clientLastName as string) || '',
      clientEmail: (referralFormData.clientEmail as string) || undefined,
      clientPhone: (referralFormData.clientPhone as string) || '',
    },
  })

  const onSubmit = async (data: ClientInfoType) => {
    if (isSubmitting) return
    setIsSubmitting(true)

    try {
      // Create consumer with the form data - validation for at least email or phone is handled by the schema
      const consumerData = {
        firstName: data.clientFirstName,
        lastName: data.clientLastName,
        primaryEmail: data.clientEmail,
        phoneNumber: data.clientPhone,
      }

      const consumerResponse = await createConsumer.mutateAsync(consumerData)

      if (!consumerResponse || !consumerResponse.id) {
        throw new Error('Failed to create consumer: Invalid response')
      }

      if (currentUser) {
        sendEvent({
          userId: currentUser.id,
          eventType: 'flow_step',
          info: {
            sessionId,
            flowType: 'send_referral',
            flowStep: 'submit_client_info',
            flowId,
            clientFirstName: data.clientFirstName,
            clientLastName: data.clientLastName,
            clientEmail: data.clientEmail,
            clientPhone: data.clientPhone,
          },
        })
      }

      // Pass both consumer ID and client info (for backward compatibility)
      // Only include non-empty values in the form data
      const formData: any = {
        consumerId: consumerResponse.id,
        clientFirstName: data.clientFirstName,
        clientLastName: data.clientLastName,
      }

      // Only add email and phone if they have values
      if (data.clientEmail) {
        formData.clientEmail = data.clientEmail
      }

      if (data.clientPhone) {
        formData.clientPhone = data.clientPhone
      }

      handleNext(formData)
    } catch (error) {
      console.error('Error creating client:', error)
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate client. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="mb-24 flex flex-col gap-1">
        <InputFormField control={form.control} name="clientFirstName" label="Client First Name" required />
        <InputFormField control={form.control} name="clientLastName" label="Client Last Name" required />
        <InputFormField control={form.control} name="clientPhone" label="Client Phone" type="tel" LeftIcon={Phone} />

        <InputFormField control={form.control} name="clientEmail" label="Client Email" type="email" LeftIcon={Mail} />

        <FixedFooter className="fixed inset-x-0">
          <div className="flex w-full flex-row gap-3">
            <Button variant="link" type="button" onClick={handleBack} className="w-full">
              Previous
            </Button>
            <Button variant="default" type="submit" className="w-full" disabled={isSubmitting} isLoading={isSubmitting}>
              Next
            </Button>
          </div>
        </FixedFooter>
      </form>
    </Form>
  )
}
